import type { ScrollbarOptions, ScrollbarState } from '@app/interfaces/smoothScrollbar.interface';
import type { Directive, DirectiveBinding } from 'vue';

const defaultOptions: Required<ScrollbarOptions> = {
	topOffset: 8,
	bottomOffset: 8,
	minThumbHeight: 8,
};

const stateMap = new WeakMap<HTMLElement, ScrollbarState>();

const resolveOptions = (binding?: DirectiveBinding<ScrollbarOptions>): Required<ScrollbarOptions> => ({
	topOffset: binding?.value?.topOffset ?? defaultOptions.topOffset,
	bottomOffset: binding?.value?.bottomOffset ?? defaultOptions.bottomOffset,
	minThumbHeight: binding?.value?.minThumbHeight ?? defaultOptions.minThumbHeight,
});

const destroyScrollbar = (el: HTMLElement): void => {
	const existing = stateMap.get(el);

	if (!existing) return;

	existing.cleanup();
	stateMap.delete(el);
};

const createScrollbar = (el: HTMLElement, binding: DirectiveBinding<ScrollbarOptions>): ScrollbarState => {
	let options = resolveOptions(binding);
	const thumb = document.createElement('div');
	thumb.className = 'SmoothScrollbar';
	thumb.setAttribute('aria-hidden', 'true');

	const shouldResetPosition = getComputedStyle(el).position === 'static' && !el.style.position;

	if (shouldResetPosition) {
		el.style.position = 'relative';
	}

	el.classList.add('hasSmoothScrollbar');
	el.appendChild(thumb);

	let scrollTimeout: number | null = null;
	let lastScrollTop = 0;
	let lastTimestamp = 0;
	let velocity = 0;
	let momentumFrame: number | null = null;

	const stopMomentum = (): void => {
		if (momentumFrame) {
			cancelAnimationFrame(momentumFrame);
			momentumFrame = null;
		}
	};

	const hideScrollbar = (): void => {
		thumb.classList.remove('visible');
		lastTimestamp = 0;
	};

	const scheduleHide = (): void => {
		if (scrollTimeout) {
			clearTimeout(scrollTimeout);
		}

		scrollTimeout = window.setTimeout(hideScrollbar, 600);
	};

	const updateScrollbar = (): void => {
		const { scrollTop, scrollHeight, clientHeight } = el;
		const canScroll = scrollHeight > clientHeight;

		if (!canScroll) {
			thumb.style.display = 'none';
			return;
		}

		thumb.style.display = 'block';

		const ratio = clientHeight / scrollHeight;
		const thumbHeight = Math.max(clientHeight * ratio, options.minThumbHeight);
		const maxScroll = scrollHeight - clientHeight;
		const trackHeight = Math.max(clientHeight - thumbHeight - options.bottomOffset - options.topOffset, 1);
		const thumbProgress = (scrollTop / maxScroll) * trackHeight;

		thumb.style.height = `${thumbHeight}px`;
		thumb.style.top = `${scrollTop + options.topOffset}px`;
		thumb.style.transform = `translateY(${thumbProgress}px)`;
	};

	const applyMomentum = (): void => {
		if (Math.abs(velocity) < 0.1) {
			velocity = 0;
			momentumFrame = null;

			stopMomentum();
			scheduleHide();
			return;
		}

		thumb.classList.add('visible');
		el.scrollTop += velocity;
		velocity *= 0.95;

		updateScrollbar();
		scheduleHide();

		momentumFrame = requestAnimationFrame(applyMomentum);
	};

	const handleScroll = (): void => {
		thumb.classList.add('visible');

		const now = performance.now();
		const currentScrollTop = el.scrollTop;

		if (lastTimestamp) {
			const timeDelta = now - lastTimestamp;
			const scrollDelta = currentScrollTop - lastScrollTop;
			velocity = (scrollDelta / timeDelta) * 16; // normalize to ~60fps
		}

		lastScrollTop = currentScrollTop;
		lastTimestamp = now;

		updateScrollbar();
		scheduleHide();
	};

	const handleScrollEnd = (): void => {
		stopMomentum();

		if (Math.abs(velocity) > 1) {
			applyMomentum();
		} else {
			scheduleHide();
		}
	};

	el.addEventListener('scroll', handleScroll, { passive: true });
	el.addEventListener('touchend', handleScrollEnd, { passive: true });
	el.addEventListener('mouseup', handleScrollEnd);
	window.addEventListener('resize', updateScrollbar);

	const cleanup = (): void => {
		el.removeEventListener('scroll', handleScroll);
		el.removeEventListener('touchend', handleScrollEnd);
		el.removeEventListener('mouseup', handleScrollEnd);
		window.removeEventListener('resize', updateScrollbar);

		if (scrollTimeout) {
			clearTimeout(scrollTimeout);
			scrollTimeout = null;
		}

		if (momentumFrame) {
			cancelAnimationFrame(momentumFrame);
			momentumFrame = null;
		}

		thumb.remove();
		el.classList.remove('hasSmoothScrollbar');

		if (shouldResetPosition) {
			el.style.position = '';
		}
	};

	const setOptions = (newBinding: DirectiveBinding<ScrollbarOptions>): void => {
		options = resolveOptions(newBinding);
		updateScrollbar();
	};

	return {
		thumb,
		update: updateScrollbar,
		setOptions,
		cleanup,
	};
};

const smoothScrollbarDirective: Directive<HTMLElement, ScrollbarOptions> = {
	mounted: (el, binding) => {
		const state = createScrollbar(el, binding);
		stateMap.set(el, state);
		state.update();
	},
	updated: (el, binding) => {
		const existing = stateMap.get(el);

		if (existing) {
			existing.setOptions(binding);
			existing.update();
			return;
		}

		const state = createScrollbar(el, binding);
		stateMap.set(el, state);
		state.update();
	},
	unmounted: (el) => {
		destroyScrollbar(el);
	},
};

export { smoothScrollbarDirective };
